import React from 'react';
import { ShieldCheck, TrendingDown, Users, Award, Clock, Wrench, CheckCircle } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const Trust = () => {
  const { formatPrice, selectedCountry, t } = useCurrency();
  
  const isIndia = selectedCountry.code === 'IN';
  
  const stats = [
    { value: '< 1.5%', label: t('trust.claimRate'), icon: TrendingDown },
    { value: '50K+', label: t('trust.inspected'), icon: ShieldCheck },
    { value: '98%', label: t('trust.satisfaction'), icon: Users },
    { value: formatPrice(45000), label: t('trust.avgSaved'), icon: Award },
  ];

  const reasons = [
    { icon: ShieldCheck, title: t('trust.reasons.catchProblems.title'), desc: t('trust.reasons.catchProblems.desc', { range: `${formatPrice(30000)}-${formatPrice(80000)}` }) },
    { icon: Wrench, title: t('trust.reasons.oemWorkshop.title'), desc: t('trust.reasons.oemWorkshop.desc') },
    { icon: Clock, title: t('trust.reasons.reportHours.title'), desc: t('trust.reasons.reportHours.desc') },
    { icon: Award, title: t('trust.reasons.negotiation.title'), desc: t('trust.reasons.negotiation.desc', { amount: formatPrice(45000) }) },
  ];

  const testimonialAuthor = isIndia ? t('trust.testimonialAuthor') : t('trust.testimonialAuthorMY');

  return (
    <section id="why-us" className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 text-center border border-slate-200 hover:shadow-lg transition-shadow">
              <stat.icon className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <div className="text-3xl font-extrabold text-slate-900 mb-1">{stat.value}</div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Reasons */}
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <ShieldCheck className="h-4 w-4" />
              {t('trust.whyChooseUs')}
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('trust.smartestInvestment')}
            </h2>
            <p className="text-slate-600 mb-6">
              {t('trust.inspectionCanSave', { price: formatPrice(1300), bigPrice: formatPrice(100000) })}
            </p>
            
            <div className="space-y-4">
              {reasons.map((reason, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-colors">
                  <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <reason.icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{reason.title}</h3>
                    <p className="text-sm text-slate-600">{reason.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl p-8">
              <img 
                src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=600"
                alt="Car inspection process"
                className="rounded-2xl shadow-xl"
              />
              {/* Testimonial Card */}
              <div className="absolute -bottom-6 -left-6 max-w-xs">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 text-sm">
                        {t('trust.testimonial', { amount: formatPrice(80000) })}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">- {testimonialAuthor}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trust;
